import { Inject, Injectable } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Action, select, Store } from '@ngrx/store'
import { catchError, distinctUntilChanged, filter, map, switchMap, takeUntil, tap, withLatestFrom } from 'rxjs/operators'
import {
  DIALOG_BODY_BATTERY_JUMP_START,
  DIALOG_BODY_BATTERY_REPLACE,
  DIALOG_BODY_HYDROGEN_ALERT,
  DIALOG_TITLE_HYDROGEN_ALERT,
  getAnalyticsIssueLabel,
} from './issue.constants'


import { AAAStore } from '../../store/root-reducer'

import {
  ISSUE_OPTIONS,
  issueOptionsFailure,
  issueOptionsSuccess,
  PACE_SETTER_NEXT,
  resetIssueOptions,
  SET_ISSUE_OPTION_SELECTED,
  SET_PACE_SETTER_SITUATION,
  setIssueOptionSelected,
  setPaceSetterStep,
} from './issue.actions'

import {
  IssueOption,
  IssueOptionCode,
  IssueOptionSelected,
  IssueOptionsResponse,
  IssueType,
  PACE_SETTER_SITUATION_TYPES
} from './issue.types'

import { Router } from '@angular/router'
import { ROUTER_NAVIGATED } from '@ngrx/router-store'
import { of } from 'rxjs'
import { ErrorReportingService } from '../../shared/services/error-reporting.service'
import { DRR_BASE_HREF } from '../../shared/shared.config'
import { DestinationType, PayloadedAction } from '../../shared/types'
import { selectEligibility, selectIsRapUser } from '../auth/auth.selectors'
import { Eligibility } from '../auth/eligibility.types'
import { selectActiveCallStatus } from '../dashboard/calls-statuses/call-status.selectors'
import { addPartialCallRequest } from '../dashboard/calls.actions'
import { AAACallStatus } from '../dashboard/calls.types'
import { setDestinationType } from '../location/aar/aar.actions'
import { RapService } from '../rap/rap.service'
import { ServicingClubService } from '../servicing-club/servicing-club.service'
import { AdobeEventService } from '../tagging/adobe/event-adobe.service'
import events from '../tagging/events'
import { TaggingService } from '../tagging/tagging.service'
import { AdobeEventTypes } from '../tagging/tagging.types'
import { closeDialog, openErrorDialog, openMessageDialog } from '../ui/ui.actions'
import { Dialog, ErrorDialogTypes, MessageDialogTypes, StepTypes } from '../ui/ui.types'
import { isAgentAppId } from '../ui/ui.utils'
import { navigateNextStep } from '../wizard/wizard.actions'
import { WizardService } from '../wizard/wizard.service'
import { selectIssueOptions, selectIssueOptionSelected } from './issue.selectors'
import { hasHiddenSubOptionAdvisories } from './issue.utils'

@Injectable()
export class IssueEffects {

  setDestinationType = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SET_PACE_SETTER_SITUATION, PACE_SETTER_NEXT, SET_ISSUE_OPTION_SELECTED),
        switchMap((action: Action) =>
          of(action).pipe(
            withLatestFrom(
              this.store$.pipe(select(selectIssueOptionSelected)),
              this.store$.pipe(select(selectIsRapUser)),
            ),
            map(([_, issueOptionSelected, isRapUser]: [
              Action,
              IssueOptionSelected,
              boolean,
            ]) => {
              let destinationType = isRapUser && !isAgentAppId() ? DestinationType.DEALER : DestinationType.AAR
              if (issueOptionSelected?.composedService?.issueType === IssueType.FUEL_ELECTRIC) {
                destinationType = DestinationType.EV_STATION
              }
              return setDestinationType({
                payload: destinationType
              })
            }
            ),
          )
        )
      )
  )

  handleNotifyPoliceDialog = createEffect(() =>
    this.actions$.pipe(
      ofType(SET_PACE_SETTER_SITUATION, SET_ISSUE_OPTION_SELECTED),
      withLatestFrom(
        this.store$.pipe(select(selectActiveCallStatus)),
      ),
      filter(
        ([action, activeCallStatus]: [PayloadedAction, AAACallStatus]) =>
          activeCallStatus === null &&
          (
            PACE_SETTER_SITUATION_TYPES.ACCIDENT === action.payload.name?.toUpperCase()
            || action.payload.option?.service?.issueType === IssueType.ACCIDENT
          )
      ),
      map(([_]) =>
        openMessageDialog({
          payload: {
            type: MessageDialogTypes.NOTIFY_POLICE,
          },
        })
      )
    )
  )

  showIssueAdvisoryAfterPrecedingDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(closeDialog),
      withLatestFrom(
        this.store$.pipe(select(selectIssueOptionSelected)),
        this.store$.pipe(select(selectEligibility)),
        this.store$.pipe(select(selectActiveCallStatus)),
      ),
      filter(([action, issueOptionSelected, eligibility, activeCallStatus]: [
        PayloadedAction,
        IssueOptionSelected | null,
        Eligibility,
        AAACallStatus | null,
      ]) =>
        Boolean(issueOptionSelected) &&
        action.payload?.type === this.precedingAdvisoryDialog(issueOptionSelected, eligibility) &&
        this.shouldShowIssueAdvisory(issueOptionSelected.option, activeCallStatus)
      ),
      map(() =>
        openMessageDialog({
          payload: {
            type: MessageDialogTypes.ISSUE_ADVISORY,
          },
        })
      )
    )
  )

  handleNavigation = createEffect(() =>
    this.actions$.pipe(
      ofType(ROUTER_NAVIGATED),
      map(
        (action: PayloadedAction) => action.payload.routerState.root.queryParams
      ),
      filter((params) => params.step === 'issue'),
      map(({ index }) => {
        if (!index) {
          this.taggingService.setPageLoadEvent({ pageType: events.issue.PAGE_TYPE, pageName: events.issue.PAGE_NAME_MENU })
        }
        return setPaceSetterStep({ index: index || 0 })
      }),
    )
  )

  showBatteryServiceDialogOnSubmit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ROUTER_NAVIGATED),
      map((action: PayloadedAction) => action.payload.routerState.root.queryParams),
      filter((params) => params.step === StepTypes.SUBMIT),
      withLatestFrom(
        this.store$.pipe(select(selectIssueOptionSelected)),
        this.store$.pipe(select(selectActiveCallStatus)),
      ),
      filter(([_, issueOptionSelected, activeCallStatus]: [
        unknown,
        IssueOptionSelected | null,
        AAACallStatus | null,
      ]) =>
        activeCallStatus === null &&
        issueOptionSelected?.option?.requiresTowing === false &&
        Boolean(this.batteryServiceDialogBody(issueOptionSelected.option.optionCode))
      ),
      map(([_, issueOptionSelected]) => issueOptionSelected),
      distinctUntilChanged(),
      map((issueOptionSelected: IssueOptionSelected) =>
        openMessageDialog({
          payload: {
            type: MessageDialogTypes.BATTERY_SERVICE,
            params: { message: this.batteryServiceDialogBody(issueOptionSelected.option.optionCode) },
          },
        })
      )
    )
  )

  requestIssueOptions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ISSUE_OPTIONS.REQUEST),
      switchMap((action: PayloadedAction) =>
        this.servicingClubService.getIssueOptions$(action.payload).pipe(
          map((issueOptions: IssueOptionsResponse) => issueOptionsSuccess({ payload: issueOptions })),
          catchError((error) => this.errorReportingService.notifyErrorObservable(error, issueOptionsFailure)),
          // A reset means the request no longer matches the Issue step (e.g. the vehicle changed
          // and is waiting for enrichment); letting it finish would show a stale error (DRRWEB-3630)
          takeUntil(this.actions$.pipe(ofType(resetIssueOptions))),
        ),
      )
    )
  )

  trackHiddenSubOptionAdvisories$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(issueOptionsSuccess),
        withLatestFrom(this.store$.pipe(select(selectIssueOptions))),
        tap(([_, issueOptions]) =>
          issueOptions?.options
            .filter(hasHiddenSubOptionAdvisories)
            .forEach((option) =>
              this.adobeEventService.sendEvent(
                {
                  eventValue: events.issue.ALL_SUB_ISSUES_DISABLED,
                  eventName: AdobeEventTypes.SYSTEM,
                },
                getAnalyticsIssueLabel(option.optionCode)
              )
            )
        )
      ),
    { dispatch: false }
  )

  processIssueOptionSelected$ = createEffect(() =>
    this.actions$.pipe(
      ofType<ReturnType<typeof setIssueOptionSelected>>(SET_ISSUE_OPTION_SELECTED),
      withLatestFrom(
        this.store$.pipe(select(selectEligibility)),
        this.store$.pipe(select(selectActiveCallStatus)),
      ),
      switchMap(([action, eligibility, activeCallStatus]: [PayloadedAction, Eligibility, AAACallStatus | null]) => {
        const precedingDialog = this.precedingAdvisoryDialog(action.payload, eligibility)
        if (precedingDialog === MessageDialogTypes.CUSTOM_PHONE) {
          return [
            openMessageDialog({
              payload: {
                type: MessageDialogTypes.CUSTOM_PHONE,
                title: DIALOG_TITLE_HYDROGEN_ALERT(),
                content: DIALOG_BODY_HYDROGEN_ALERT(),
              },
            })
          ]
        }
        if (precedingDialog === ErrorDialogTypes.NOT_ELIGIBLE) {
          return [
            openErrorDialog({
              payload: {
                type: ErrorDialogTypes.NOT_ELIGIBLE
              }
            })
          ]
        }
        const actions: Action[] = [
          navigateNextStep(),
          addPartialCallRequest(),
        ]
        if (!precedingDialog && this.shouldShowIssueAdvisory(action.payload.option, activeCallStatus)) {
          actions.push(
            openMessageDialog({
              payload: {
                type: MessageDialogTypes.ISSUE_ADVISORY,
              },
            })
          )
        }
        return actions
      })
    )
  )

  private precedingAdvisoryDialog(
    issueOptionSelected: IssueOptionSelected,
    eligibility: Eligibility
  ): Dialog['type'] | null {
    if (issueOptionSelected.option?.optionCode === IssueOptionCode.HYDROGEN_ALTERNATE) {
      return MessageDialogTypes.CUSTOM_PHONE
    }
    if (this.rapService.isRapNotEligible(issueOptionSelected, eligibility)) {
      return ErrorDialogTypes.NOT_ELIGIBLE
    }
    if (issueOptionSelected.option?.service?.issueType === IssueType.ACCIDENT) {
      return MessageDialogTypes.NOTIFY_POLICE
    }
    return null
  }

  private batteryServiceDialogBody(optionCode?: IssueOptionCode): string | null {
    switch (optionCode) {
      case IssueOptionCode.NEED_A_TOW_BATTERY_REPLACE:
        return DIALOG_BODY_BATTERY_REPLACE()
      case IssueOptionCode.NEED_A_TOW_BATTERY_JUMP_START:
        return DIALOG_BODY_BATTERY_JUMP_START()
      default:
        return null
    }
  }

  private shouldShowIssueAdvisory(option: IssueOption, activeCallStatus: AAACallStatus | null): boolean {
    return activeCallStatus === null &&
      Boolean(option?.enabled) &&
      Boolean(option?.advisoryMessages?.length)
  }

  constructor(
    private actions$: Actions,
    private router: Router,
    private store$: Store<AAAStore>,
    private wizardService: WizardService,
    private errorReportingService: ErrorReportingService,
    private taggingService: TaggingService,
    private adobeEventService: AdobeEventService,
    private servicingClubService: ServicingClubService,
    private rapService: RapService,
    @Inject(DRR_BASE_HREF) private drrBaseHref: string
  ) {}
}
